import { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '@stores/gameStore';
import { useRGS } from '@hooks/useRGS';
import { GAME_CONFIG } from '@lib/constants';
import { formatAmount, formatMinorUnits } from '@/utils/format-amount';

interface BetFormState {
  amount: number;
  autoCashout: number | null;
  autoCashoutEnabled: boolean;
}

const QUICK_BET_AMOUNTS = [1, 5, 10, 25, 50, 100];

const initialBetState: BetFormState = {
  amount: 10,
  autoCashout: 2.0,
  autoCashoutEnabled: false,
};

/**
 * Betting panel component
 * Handles bet amount input, quick bets, auto-cashout settings
 */
export function BettingPanel(): React.JSX.Element {
  const { balance, gamePhase, currentBet, currency, currencyDecimals } = useGameStore();
  const { placeBet } = useRGS();

  // Bet form state
  const [betState, setBetState] = useState<BetFormState>(initialBetState);

  // Validation
  const validation = useMemo(() => {
    const errors: string[] = [];
    const amount = betState.amount;

    // R22-3: MIN_BET/MAX_BET are stored as minor units (cents); compare in
    // minor units and render through formatMinorUnits for currency-aware
    // precision. Balance is also minor units.
    const amountMinor = Math.round(amount * 100);
    if (amountMinor < GAME_CONFIG.MIN_BET) {
      errors.push(`Min bet: ${formatMinorUnits(GAME_CONFIG.MIN_BET, currency, currencyDecimals ?? undefined)}`);
    }
    if (amountMinor > GAME_CONFIG.MAX_BET) {
      errors.push(`Max bet: ${formatMinorUnits(GAME_CONFIG.MAX_BET, currency, currencyDecimals ?? undefined)}`);
    }
    if (amountMinor > balance) {
      errors.push('Insufficient balance');
    }
    if (betState.autoCashoutEnabled && betState.autoCashout !== null) {
      if (betState.autoCashout < 1.01) {
        errors.push('Auto cashout min: 1.01x');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [betState, balance, currency, currencyDecimals]);

  // Can place bet?
  const canPlaceBet = useMemo(() => {
    return (
      gamePhase === 'betting' &&
      validation.isValid &&
      !currentBet &&
      betState.amount > 0
    );
  }, [gamePhase, validation.isValid, currentBet, betState.amount]);

  // Handlers
  const handleAmountChange = useCallback(
    (value: number): void => {
      setBetState((prev) => ({
        ...prev,
        amount: Math.max(0, value),
      }));
    },
    []
  );

  const handleQuickBet = useCallback(
    (amount: number): void => {
      setBetState((prev) => ({
        ...prev,
        amount,
      }));
    },
    []
  );

  // R22-3: minor-to-major conversion factor driven by currency precision
  // (was hardcoded /100, which silently capped BTC precision at 2 decimals).
  const minorToMajor = Math.pow(10, currencyDecimals ?? 2);

  const handleMaxBet = useCallback((): void => {
    const maxAmount = Math.min(balance / minorToMajor, GAME_CONFIG.MAX_BET / minorToMajor);
    setBetState((prev) => ({
      ...prev,
      amount: maxAmount,
    }));
  }, [balance, minorToMajor]);

  const handleHalfBet = useCallback((): void => {
    setBetState((prev) => ({
      ...prev,
      amount: Math.max(GAME_CONFIG.MIN_BET / minorToMajor, prev.amount / 2),
    }));
  }, [minorToMajor]);

  const handleDoubleBet = useCallback((): void => {
    const maxAmount = Math.min(balance / minorToMajor, GAME_CONFIG.MAX_BET / minorToMajor);
    setBetState((prev) => ({
      ...prev,
      amount: Math.min(maxAmount, prev.amount * 2),
    }));
  }, [balance, minorToMajor]);

  const handleAutoCashoutToggle = useCallback((): void => {
    setBetState((prev) => ({
      ...prev,
      autoCashoutEnabled: !prev.autoCashoutEnabled,
    }));
  }, []);

  const handleAutoCashoutChange = useCallback(
    (value: number): void => {
      setBetState((prev) => ({
        ...prev,
        autoCashout: Math.max(1.01, value),
      }));
    },
    []
  );

  const handlePlaceBet = useCallback((): void => {
    if (!canPlaceBet) return;

    // R22-3: convert major-unit input to minor units using the advertised
    // precision, not a hardcoded *100 (which would drop sat-level precision
    // for BTC or wei-level precision for ETH).
    //
    // NOTE: this preserves existing wire-format behaviour (the RGS still
    // expects a `number` of minor units). For crypto operators beyond
    // Number.MAX_SAFE_INTEGER (e.g. wei), a follow-up PR should widen the
    // RGS contract to accept a stringified bigint (tracked in Phase 22 /
    // R22-1 on the RGS side).
    // Fallback to 2 decimals for unknown/legacy currencies (matches the
    // pre-R22-3 hardcoded *100 behaviour for EUR/USD operators).
    const decimals = currencyDecimals ?? 2;
    const amountInMinor = Math.round(betState.amount * Math.pow(10, decimals));
    const autoCashout = betState.autoCashoutEnabled
      ? betState.autoCashout ?? undefined
      : undefined;

    placeBet(amountInMinor, autoCashout);
  }, [canPlaceBet, betState, placeBet, currencyDecimals]);

  // Format balance for display (R22-3: currency-aware, minor-units input)
  const formattedBalance = formatMinorUnits(balance, currency, currencyDecimals ?? undefined);

  return (
    <div className="w-full max-w-md rounded-lg bg-aviator-skyDark/95 p-4 shadow-2xl backdrop-blur-sm">
      {/* Balance display */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-white/70">Balance</span>
        <span className="text-xl font-bold text-aviator-gold">${formattedBalance}</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Bet amount input */}
        <div className="mb-4">
          <label className="mb-1 block text-xs text-white/70">Bet Amount</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { handleAmountChange(betState.amount - 1); }}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-aviator-danger/80 text-xl font-bold text-white transition-colors hover:bg-aviator-danger"
              disabled={gamePhase !== 'betting'}
            >
              -
            </button>
            <input
              type="number"
              value={betState.amount}
              onChange={(e) => { handleAmountChange(parseFloat(e.target.value) || 0); }}
              className="h-10 flex-1 rounded-md border-2 border-aviator-gold/30 bg-aviator-skyDark/50 px-3 text-center text-xl font-bold text-white focus:border-aviator-gold focus:outline-none"
              disabled={gamePhase !== 'betting'}
              min={GAME_CONFIG.MIN_BET / 100}
              max={GAME_CONFIG.MAX_BET / 100}
              step="0.1"
            />
            <button
              onClick={() => { handleAmountChange(betState.amount + 1); }}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-aviator-success/80 text-xl font-bold text-white transition-colors hover:bg-aviator-success"
              disabled={gamePhase !== 'betting'}
            >
              +
            </button>
          </div>
        </div>

        {/* Quick bet buttons */}
        <div className="mb-4 grid grid-cols-6 gap-1">
          {QUICK_BET_AMOUNTS.map((amount) => (
            <button
              key={amount}
              onClick={() => { handleQuickBet(amount); }}
              className={`rounded py-2 text-xs font-bold transition-all ${
                betState.amount === amount
                  ? 'bg-aviator-gold text-aviator-skyDark'
                  : 'bg-aviator-sky/20 text-white hover:bg-aviator-sky/40'
              }`}
              disabled={gamePhase !== 'betting'}
            >
              {amount}
            </button>
          ))}
        </div>

        {/* Bet modifiers */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={handleHalfBet}
            className="flex-1 rounded-md bg-aviator-sky/20 py-2 text-xs font-bold text-white transition-colors hover:bg-aviator-sky/40"
            disabled={gamePhase !== 'betting'}
          >
            1/2
          </button>
          <button
            onClick={handleDoubleBet}
            className="flex-1 rounded-md bg-aviator-sky/20 py-2 text-xs font-bold text-white transition-colors hover:bg-aviator-sky/40"
            disabled={gamePhase !== 'betting'}
          >
            2x
          </button>
          <button
            onClick={handleMaxBet}
            className="flex-1 rounded-md bg-aviator-sky/20 py-2 text-xs font-bold text-white transition-colors hover:bg-aviator-sky/40"
            disabled={gamePhase !== 'betting'}
          >
            MAX
          </button>
        </div>

        {/* Auto cashout */}
        <div className="mb-4 rounded-md bg-aviator-sky/10 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-white">Auto Cashout</span>
            <button
              onClick={handleAutoCashoutToggle}
              className={`relative h-6 w-12 rounded-full transition-colors ${
                betState.autoCashoutEnabled ? 'bg-aviator-gold' : 'bg-aviator-skyDark/50'
              }`}
              disabled={gamePhase !== 'betting'}
            >
              <motion.div
                animate={{ x: betState.autoCashoutEnabled ? 24 : 2 }}
                className="absolute top-1 h-4 w-4 rounded-full bg-white shadow"
              />
            </button>
          </div>
          {betState.autoCashoutEnabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={betState.autoCashout ?? 2}
                  onChange={(e) => { handleAutoCashoutChange(parseFloat(e.target.value) || 1.01); }}
                  className="h-8 w-full rounded border-2 border-aviator-gold/30 bg-aviator-skyDark/50 px-2 text-center font-bold text-white focus:border-aviator-gold focus:outline-none"
                  disabled={gamePhase !== 'betting'}
                  min={1.01}
                  step={0.1}
                />
                <span className="text-white">x</span>
              </div>
            </motion.div>
          )}
        </div>

        {/* Validation errors */}
        {!validation.isValid && (
          <div className="mb-4 rounded-md bg-aviator-danger/30 p-2">
            {validation.errors.map((error, i) => (
              <p key={i} className="text-xs text-red-300">
                {error}
              </p>
            ))}
          </div>
        )}

        {/* Place bet button */}
        <motion.button
          whileHover={canPlaceBet ? { scale: 1.02 } : {}}
          whileTap={canPlaceBet ? { scale: 0.98 } : {}}
          onClick={handlePlaceBet}
          disabled={!canPlaceBet}
          className={`w-full rounded-lg py-4 text-xl font-bold uppercase shadow-lg transition-all ${
            canPlaceBet
              ? 'bg-gradient-to-r from-aviator-success to-green-600 text-white hover:from-green-400 hover:to-aviator-success'
              : 'cursor-not-allowed bg-gray-600/50 text-gray-400'
          }`}
        >
          {currentBet
            ? 'BET PLACED'
            : gamePhase === 'betting'
              ? `PLACE BET ($${formatAmount(betState.amount, currency, currencyDecimals ?? undefined)})`
              : 'WAIT FOR NEXT ROUND'}
        </motion.button>

        {/* Potential win display */}
        {betState.autoCashoutEnabled && betState.autoCashout && (
          <p className="mt-2 text-center text-sm text-white/70">
            Auto cashout at {betState.autoCashout.toFixed(2)}x ={' '}
            <span className="text-aviator-gold">
              ${formatAmount(betState.amount * betState.autoCashout, currency, currencyDecimals ?? undefined)}
            </span>
          </p>
        )}
      </motion.div>
    </div>
  );
}
